// Period Selector Component
import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DateRange, ReportPeriod } from '@/types/report';
import { PERIOD_OPTIONS, getCustomRange } from '../utils/periods';

interface PeriodSelectorProps {
  value: DateRange;
  onChange: (period: DateRange) => void;
}

export function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<ReportPeriod>('month');
  const [showCustom, setShowCustom] = useState(false);

  const handlePeriodChange = (period: ReportPeriod) => {
    setSelectedPeriod(period);
    
    if (period === 'custom') {
      setShowCustom(true);
      return;
    }

    const option = PERIOD_OPTIONS.find(opt => opt.value === period);
    if (option) {
      onChange(option.getDates());
      setShowCustom(false);
    }
  };

  const handleCustomDateChange = (start: string, end: string) => {
    if (start && end) {
      onChange(getCustomRange(new Date(start), new Date(end)));
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-card border border-border rounded-lg">
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">
          الفترة / Période
        </span>
      </div>

      <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PERIOD_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                <span className="font-arabic">{option.labelAr}</span>
                <span className="text-muted-foreground">/</span>
                <span>{option.label}</span>
              </span>
            </SelectItem>
          ))}
          <SelectItem value="custom">
            <span className="flex items-center gap-2">
              <span className="font-arabic">مخصص</span>
              <span className="text-muted-foreground">/</span>
              <span>Personnalisé</span>
            </span>
          </SelectItem>
        </SelectContent>
      </Select>

      {showCustom && (
        <div className="flex flex-col gap-2 pt-2 border-t border-border">
          <label className="text-sm text-muted-foreground">
            من / Du
          </label>
          <input
            type="date"
            className="px-3 py-2 bg-background border border-border rounded-md text-foreground"
            defaultValue={value.start.toISOString().split('T')[0]}
            onChange={(e) => handleCustomDateChange(e.target.value, value.end.toISOString().split('T')[0])}
          />
          
          <label className="text-sm text-muted-foreground">
            إلى / Au
          </label>
          <input
            type="date"
            className="px-3 py-2 bg-background border border-border rounded-md text-foreground"
            defaultValue={value.end.toISOString().split('T')[0]}
            onChange={(e) => handleCustomDateChange(value.start.toISOString().split('T')[0], e.target.value)}
          />
        </div>
      )}

      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
        {value.start.toLocaleDateString('fr-DZ')} - {value.end.toLocaleDateString('fr-DZ')}
      </div>
    </div>
  );
}
